/**
 * Ad Account Manager API Client
 * E007-S029: Settings page for managing connected ad accounts
 */

import { apiClient } from './apiClient';
import type {
  AdAccount,
  AdAccountListResponse,
  OAuthInitiateResponse,
  OAuthCallbackResponse,
  ActivateAccountsRequest,
  ActivateAccountsResponse,
  UpdateAccountRequest,
  AuditLogResponse,
  Platform,
} from '../types/ad-accounts';

const BASE_PATH = '/ad-accounts';

/**
 * List all connected ad accounts grouped by platform
 */
export async function listAdAccounts(): Promise<AdAccountListResponse> {
  const response = await apiClient.get<AdAccountListResponse>(BASE_PATH);
  return response.data;
}

/**
 * Get a single ad account by ID
 */
export async function getAdAccount(accountId: string): Promise<AdAccount> {
  const response = await apiClient.get<AdAccount>(`${BASE_PATH}/${accountId}`);
  return response.data;
}

/**
 * Initiate OAuth flow for connecting a platform
 * Returns URL to redirect user to for authorization
 */
export async function initiateOAuth(platform: Platform): Promise<OAuthInitiateResponse> {
  const response = await apiClient.post<OAuthInitiateResponse>(
    `${BASE_PATH}/connect/${platform}`
  );
  return response.data;
}

/**
 * Handle OAuth callback (exchange code for tokens)
 * Returns available accounts to connect
 */
export async function handleOAuthCallback(
  platform: Platform,
  code: string,
  state: string
): Promise<OAuthCallbackResponse> {
  const response = await apiClient.post<OAuthCallbackResponse>(
    `${BASE_PATH}/callback/${platform}`,
    { code, state }
  );
  return response.data;
}

/**
 * Activate selected accounts after OAuth
 */
export async function activateAccounts(
  request: ActivateAccountsRequest
): Promise<ActivateAccountsResponse> {
  const response = await apiClient.post<ActivateAccountsResponse>(
    BASE_PATH,
    request
  );
  return response.data;
}

/**
 * Update account type and/or configuration
 */
export async function updateAccount(
  accountId: string,
  request: UpdateAccountRequest
): Promise<AdAccount> {
  const response = await apiClient.patch<AdAccount>(
    `${BASE_PATH}/${accountId}`,
    request
  );
  return response.data;
}

/**
 * Disconnect an ad account
 */
export async function disconnectAccount(accountId: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${accountId}`);
}

/**
 * Initiate reauthentication for expired/revoked tokens
 */
export async function reauthenticateAccount(accountId: string): Promise<OAuthInitiateResponse> {
  const response = await apiClient.post<OAuthInitiateResponse>(
    `${BASE_PATH}/${accountId}/reauthenticate`
  );
  return response.data;
}

/**
 * Get audit log for ad account operations
 */
export async function getAuditLog(
  accountId?: string,
  limit: number = 50
): Promise<AuditLogResponse> {
  const params = new URLSearchParams();
  if (accountId) params.append('account_id', accountId);
  params.append('limit', limit.toString());
  
  const response = await apiClient.get<AuditLogResponse>(
    `${BASE_PATH}/audit-log?${params.toString()}`
  );
  return response.data;
}

// ============================================================================
// MOCK OAUTH HELPERS (For Local Development)
// ============================================================================

/**
 * Simulate OAuth flow for local development
 * In production, this redirects to actual Meta/Google/TikTok OAuth
 */
export async function simulateOAuthFlow(platform: Platform): Promise<{
  success: boolean;
  accounts: Array<{ id: string; name: string }>;
}> {
  // 1. Get OAuth URL
  const oauthResponse = await initiateOAuth(platform);
  
  // 2. In local dev, simulate user authorization by calling callback directly
  const callbackResponse = await handleOAuthCallback(
    platform,
    `mock_code_${platform}`,
    oauthResponse.state
  );
  
  return {
    success: true,
    accounts: callbackResponse.available_accounts.map(a => ({
      id: a.id,
      name: a.name
    }))
  };
}

/**
 * Full connect flow - simulate OAuth and activate accounts
 */
export async function connectAccountsFlow(
  platform: Platform,
  selectedAccountIds: string[]
): Promise<ActivateAccountsResponse> {
  return activateAccounts({
    platform,
    account_ids: selectedAccountIds
  });
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format currency for display
 */
export function formatSpend(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Check if token is expiring soon (within 7 days)
 */
export function isTokenExpiringSoon(expiresAt?: string): boolean {
  if (!expiresAt) return false;
  const expiryDate = new Date(expiresAt);
  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
  return expiryDate < sevenDaysFromNow;
}

/**
 * Get status message for account
 */
export function getStatusMessage(account: AdAccount): string {
  if (account.status === 'disconnected') {
    return 'Token revoked or expired. Reauthenticate to reconnect.';
  }
  if (account.status === 'warning') {
    if (isTokenExpiringSoon(account.token_expires_at)) {
      return 'Token expiring soon. Consider reauthenticating.';
    }
    return 'There may be issues with this account. Check the platform for details.';
  }
  return 'Account is healthy and syncing data.';
}

/**
 * Format date for display
 */
export function formatDate(dateStr?: string): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
